import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ColumnFilterRow, applyColumnFilters } from "../../Components/Common/DashboardKit";
import useTableSort from "../../Components/Common/DashboardKit/useTableSort";
import SortableTh from "../../Components/Common/DashboardKit/SortableTh";
import {
    Container, Row, Col, Card, CardBody,
    Input, Button, Badge, Spinner,
} from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    DashboardPageHeader,
    DataTableShell,
} from '../../Components/Common/DashboardKit';
import api from '../../api';
import useAuth from '../Personal Attendance/useAuth';

/**
 * Employee EPO Limit Assignment (IT Admin only)
 *
 *   - Lists every employee with their current role + EPO limit.
 *   - Search across EmpID / FirstName / LastName / Name / Company /
 *     RoleName / EpoLimit.
 *   - Inline numeric input + Save button per row.
 *   - "Unassigned" badge for NULL limit; can be cleared back to NULL.
 *
 * Backend endpoints:
 *   GET  /api/admin/employees-with-epo-limits
 *   PUT  /api/admin/employees/:empId/epo-limit   body: { epoLimit: number | null }
 *
 * The route is wrapped in <RoleProtected requireItAdmin> in allRoutes.js.
 * This page also performs a belt-and-suspenders client check.
 *
 * What this writes:
 *   ONLY empbasic.number01. No other employee field is modified.
 *   Setting a value makes EpoLimitService return that exact amount,
 *   bypassing the legacy hardcoded fallback for that user.
 *   Clearing (NULL) restores the legacy formula behavior.
 *
 * Pass 3 UI update (2026):
 *  - Migrated the CardHeader / search / refresh / loading / error /
 *    empty / table blocks to the shared DataTableShell. Same skeleton
 *    behavior, same error message, same empty message wording.
 *  - The per-row numeric input + dirty-state detection + Save button
 *    are passed as children unchanged. The validation rules
 *    ("non-negative number or leave blank") are preserved exactly.
 *  - loadAll, handleSave, every API endpoint, every payload, and the
 *    optimistic update / rollback logic are unchanged.
 */
const EmployeeEpoLimitAssignment = () => {
    const { isItAdmin } = useAuth();

    // ── data ──
    const [employees, setEmployees] = useState([]);

    // ── ui ──
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [search, setSearch] = useState('');

    // pending edits + per-row saving state, keyed by empId
    // value is a string (so we can distinguish "not edited" from edited-to-blank)
    const [pendingLimit, setPendingLimit] = useState({}); // { [empId]: string }
    const [savingId, setSavingId] = useState(null);

    // Round 11 (Batch B) — bulk EPO-limit assignment (multi-select + one amount)
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkAmount, setBulkAmount] = useState('');
    const [bulkSaving, setBulkSaving] = useState(false);
    const [bulkMsg, setBulkMsg] = useState(null);
    const [bulkErr, setBulkErr] = useState(null);

    const toggleSelected = (empId) => setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(empId)) next.delete(empId); else next.add(empId);
        return next;
    });

    // ── USA President/CEO approval threshold (configurable) ──
    const [threshold, setThreshold] = useState('');
    const [thresholdOriginal, setThresholdOriginal] = useState(null);
    const [thresholdLoading, setThresholdLoading] = useState(true);
    const [thresholdSaving, setThresholdSaving] = useState(false);
    const [thresholdError, setThresholdError] = useState(null);

    // ── Mileage rate (Batch F) — admin-configured per-mile reimbursement ──
    const [mileageRate, setMileageRate] = useState('');
    const [mileageRateOriginal, setMileageRateOriginal] = useState(null);
    const [mileageLoading, setMileageLoading] = useState(true);
    const [mileageSaving, setMileageSaving] = useState(false);
    const [mileageError, setMileageError] = useState(null);

    // ─────────────────────────────────────────────────────────────────────
    // Load employees (with current EPO limit) on mount.
    // ─────────────────────────────────────────────────────────────────────
    const loadAll = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const res = await api.get('admin/employees-with-epo-limits');
            setEmployees(res.data || []);
        } catch (err) {
            const status = err?.response?.status;
            const msg = status === 403 || status === 401
                ? 'You are not authorized to view this page.'
                : (err?.response?.data?.error || err?.message || 'Failed to load data.');
            setLoadError(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isItAdmin) loadAll();
        else setLoading(false);
    }, [isItAdmin, loadAll]);

    // ── load/save the configurable USA President/CEO threshold ──
    const loadThreshold = useCallback(async () => {
        setThresholdLoading(true);
        setThresholdError(null);
        try {
            const res = await api.get('epo-config/usa-president-ceo-threshold');
            const val = res?.data?.threshold;
            const num = val == null ? null : Number(val);
            setThresholdOriginal(num);
            setThreshold(num == null ? '' : String(num));
        } catch (err) {
            const status = err?.response?.status;
            setThresholdError(
                status === 403 || status === 401
                    ? 'You are not authorized to view the threshold.'
                    : (err?.response?.data?.error || err?.message || 'Failed to load threshold.')
            );
        } finally {
            setThresholdLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isItAdmin) loadThreshold();
        else setThresholdLoading(false);
    }, [isItAdmin, loadThreshold]);

    const loadMileageRate = useCallback(async () => {
        setMileageLoading(true);
        setMileageError(null);
        try {
            const res = await api.get('Epo/mileage-rate');
            const val = res?.data?.rate;
            const num = val == null ? null : Number(val);
            setMileageRateOriginal(num);
            setMileageRate(num == null ? '' : String(num));
        } catch (err) {
            setMileageError(err?.response?.data?.error || err?.message || 'Failed to load mileage rate.');
        } finally {
            setMileageLoading(false);
        }
    }, []);

    useEffect(() => {
        // read is open to all; load regardless of admin so the current value shows
        loadMileageRate();
    }, [loadMileageRate]);

    const handleSaveMileageRate = async () => {
        const trimmed = (mileageRate ?? '').toString().trim();
        const num = Number(trimmed);
        if (trimmed === '' || !Number.isFinite(num) || num < 0) {
            toast.error('Enter a non-negative mileage rate.');
            return;
        }
        if (mileageRateOriginal != null && num === Number(mileageRateOriginal)) {
            toast.info('No change.');
            return;
        }
        setMileageSaving(true);
        try {
            const res = await api.put('epo-config/mileage-rate', { rate: num });
            const saved = res?.data?.rate;
            const savedNum = saved == null ? num : Number(saved);
            setMileageRateOriginal(savedNum);
            setMileageRate(String(savedNum));
            toast.success(`Mileage rate set to $${savedNum.toFixed(2)}/mile.`);
        } catch (err) {
            const status = err?.response?.status;
            toast.error(
                err?.response?.data?.error
                || (status === 403 ? 'Only IT Admin may change the mileage rate.' : (err?.message || 'Failed to save mileage rate.'))
            );
        } finally {
            setMileageSaving(false);
        }
    };

    const handleSaveThreshold = async () => {
        const trimmed = (threshold ?? '').toString().trim();
        const num = Number(trimmed);
        if (trimmed === '' || !Number.isFinite(num) || num < 0) {
            toast.error('Enter a non-negative threshold amount.');
            return;
        }
        if (thresholdOriginal != null && num === Number(thresholdOriginal)) {
            toast.info('No change.');
            return;
        }
        setThresholdSaving(true);
        try {
            const res = await api.put('epo-config/usa-president-ceo-threshold', { threshold: num });
            const saved = res?.data?.threshold;
            const savedNum = saved == null ? num : Number(saved);
            setThresholdOriginal(savedNum);
            setThreshold(String(savedNum));
            toast.success(`USA President/CEO threshold set to $${savedNum.toLocaleString()}.`);
        } catch (err) {
            const status = err?.response?.status;
            toast.error(
                err?.response?.data?.error
                || (status === 403 ? 'Not authorized to change the threshold.' : 'Failed to save threshold.')
            );
        } finally {
            setThresholdSaving(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────
    // Search filter — case-insensitive.
    // ─────────────────────────────────────────────────────────────────────
    // Round 34 — EPO-style per-column filters + click-to-sort (the shell's
    // free-text search is replaced by the filter row).
    const [columnFilters, setColumnFilters] = useState({});
    const tblColumns = useMemo(() => [
        { id: "empId", label: "Emp ID", type: "text", get: (e) => String(e.empId ?? "") },
        { id: "name", label: "Name", type: "text", get: (e) => e.name || `${e.firstName ?? ""} ${e.lastName ?? ""}`.trim() },
        { id: "company", label: "Company", type: "text", get: (e) => e.company || "" },
        { id: "dept", label: "Dept", type: "text", get: (e) => e.dept || e.department || "" },
        { id: "status", label: "Status", type: "text", get: (e) => e.status || "" },
        { id: "role", label: "Role", type: "text", get: (e) => e.roleName || "" },
        { id: "epoLimit", label: "Current EPO Limit", type: "text", get: (e) => String(e.epoLimit ?? "") },
        { id: "extra", label: "", type: "none" },
        { id: "act",   label: "Action", type: "none" },
    ], []);
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return employees;
        return employees.filter((e) => {
            const haystack = [
                e.empId, e.firstName, e.lastName, e.name, e.company,
                e.roleName,
                e.epoLimit == null ? 'unassigned' : String(e.epoLimit),
            ].filter(Boolean).join(' ').toLowerCase();
            return haystack.includes(q);
        });
    }, [employees, search]);
    const filteredBase = applyColumnFilters(filtered, tblColumns, columnFilters);
    const { sorted: view, sort, toggleSort } = useTableSort(filteredBase, tblColumns);

    // ─────────────────────────────────────────────────────────────────────
    // Save handler — only updates number01, optimistically reflects new
    // value, rolls back on error.
    // ─────────────────────────────────────────────────────────────────────
    const handleSave = async (emp) => {
        const raw = pendingLimit[emp.empId];
        const trimmed = (raw ?? '').toString().trim();
        // empty string = clear back to NULL
        const newLimit = trimmed === '' ? null : Number(trimmed);

        if (trimmed !== '' && (!Number.isFinite(newLimit) || newLimit < 0)) {
            toast.error('Enter a non-negative number or leave blank to clear.');
            return;
        }
        if (newLimit === (emp.epoLimit == null ? null : Number(emp.epoLimit))) {
            toast.info('No change.');
            return;
        }

        setSavingId(emp.empId);
        try {
            const res = await api.put(
                `admin/employees/${encodeURIComponent(emp.empId)}/epo-limit`,
                { epoLimit: newLimit }
            );
            const updated = res.data;
            setEmployees((list) =>
                list.map((e) => e.empId === emp.empId
                    ? { ...e, epoLimit: updated.epoLimit ?? null }
                    : e
                )
            );
            setPendingLimit((p) => {
                const next = { ...p };
                delete next[emp.empId];
                return next;
            });
            toast.success(
                newLimit == null
                    ? `Cleared EPO limit for ${emp.empId}.`
                    : `Set ${emp.empId} EPO limit to $${newLimit.toLocaleString()}.`
            );
        } catch (err) {
            const status = err?.response?.status;
            const errMsg = err?.response?.data?.error
                || (status === 403 ? 'Not authorized to update EPO limits.' : 'Failed to update EPO limit.');
            toast.error(errMsg);
        } finally {
            setSavingId(null);
        }
    };

    // ─── early-out for non-IT-Admin (defensive; route guard usually handles this) ──
    if (!isItAdmin) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Card>
                        <CardBody className="text-center p-5">
                            <i className="ri-shield-cross-line text-warning" style={{ fontSize: 48 }} />
                            <h5 className="mt-3">Access Denied</h5>
                            <p className="text-muted mb-0">
                                You must be an IT Admin to view this page.
                            </p>
                        </CardBody>
                    </Card>
                </Container>
            </div>
        );
    }

    const allVisibleSelected = view.length > 0 && view.every((e) => selectedIds.has(e.empId));
    const toggleSelectAllVisible = () => setSelectedIds((prev) => {
        const next = new Set(prev);
        if (allVisibleSelected) { view.forEach((e) => next.delete(e.empId)); }
        else { view.forEach((e) => next.add(e.empId)); }
        return next;
    });

    // Bulk apply: set the SAME limit (or clear) for every selected employee.
    const handleBulkApply = async () => {
        setBulkErr(null); setBulkMsg(null);
        const ids = Array.from(selectedIds);
        if (ids.length === 0) { setBulkErr('Select at least one employee.'); return; }
        const t = bulkAmount.toString().trim();
        let epoLimit = null;            // blank = clear
        if (t !== '') {
            const n = Number(t);
            if (!Number.isFinite(n) || n < 0) { setBulkErr('Enter a valid non-negative amount, or leave blank to clear.'); return; }
            epoLimit = n;
        }
        setBulkSaving(true);
        try {
            const res = await api.put('admin/employees/epo-limit/bulk', { empIds: ids, epoLimit });
            const updated = res.data?.updatedCount ?? ids.length;
            setBulkMsg(`Updated ${updated} employee(s)${epoLimit == null ? ' (cleared)' : ` to $${epoLimit.toLocaleString()}`}.`);
            setSelectedIds(new Set());
            setBulkAmount('');
            await loadAll();            // refresh to show new values
        } catch (e) {
            setBulkErr(e?.response?.data?.error || e?.message || 'Bulk update failed.');
        } finally {
            setBulkSaving(false);
        }
    };

    // Bulk toolbar shown above the table.
    const bulkToolbar = (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2 p-2 border rounded bg-light">
            <span className="fw-semibold small">{selectedIds.size} selected</span>
            <Input
                type="number" min="0" step="100" bsSize="sm" style={{ maxWidth: 160 }}
                placeholder="Amount (blank = clear)"
                value={bulkAmount}
                onChange={(e) => setBulkAmount(e.target.value)}
                disabled={bulkSaving}
            />
            <Button color="primary" size="sm" disabled={bulkSaving || selectedIds.size === 0} onClick={handleBulkApply}>
                {bulkSaving ? 'Applying…' : 'Apply to selected'}
            </Button>
            {selectedIds.size > 0 && (
                <Button color="link" size="sm" className="text-muted" onClick={() => setSelectedIds(new Set())}>Clear selection</Button>
            )}
            {bulkMsg && <span className="text-success small">{bulkMsg}</span>}
            {bulkErr && <span className="text-danger small">{bulkErr}</span>}
        </div>
    );

    // Actual <table> body — wrapped by DataTableShell. The shell shows
    // loading / error / empty states above this body when those flags
    // are active.
    const tableBody = (
        <div className="table-responsive">
            <table className="table align-middle table-nowrap table-hover mb-0">
                <thead className="table-light">
                    <tr>
                        <th style={{ width: 34 }}>
                            <Input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAllVisible} title="Select all visible" />
                        </th>
                        {tblColumns.slice(0, -2).map((c) => (
                            <SortableTh key={c.id} col={c} sort={sort} onToggle={toggleSort} />
                        ))}
                        <th style={{ minWidth: 180 }}>New EPO Limit</th>
                        <th className="text-end">Action</th>
                    </tr>
                    <ColumnFilterRow columns={[{ id: "_select", type: "none" }, ...tblColumns]} filters={columnFilters} onChange={setColumnFilters} />
                </thead>
                <tbody>
                    {view.map((e) => {
                        const pending = pendingLimit[e.empId];
                        const inputValue =
                            pending !== undefined
                                ? pending
                                : (e.epoLimit == null ? '' : String(e.epoLimit));
                        const currentNum = e.epoLimit == null ? null : Number(e.epoLimit);
                        let pendingNum = null;
                        let pendingValid = true;
                        if (pending !== undefined) {
                            const t = pending.toString().trim();
                            if (t === '') {
                                pendingNum = null;
                            } else {
                                const n = Number(t);
                                if (Number.isFinite(n) && n >= 0) {
                                    pendingNum = n;
                                } else {
                                    pendingValid = false;
                                }
                            }
                        }
                        const dirty = pending !== undefined
                            && pendingValid
                            && pendingNum !== currentNum;

                        return (
                            <tr key={e.empId} className={selectedIds.has(e.empId) ? 'table-active' : ''}>
                                <td>
                                    <Input type="checkbox" checked={selectedIds.has(e.empId)} onChange={() => toggleSelected(e.empId)} />
                                </td>
                                <td><code>{e.empId}</code></td>
                                <td>
                                    {e.name
                                        || `${e.firstName ?? ''} ${e.lastName ?? ''}`.trim()
                                        || <span className="text-muted">—</span>}
                                </td>
                                <td>{e.company || <span className="text-muted">—</span>}</td>
                                <td>{e.jcDept || <span className="text-muted">—</span>}</td>
                                <td>
                                    {e.empStatus
                                        ? <Badge color="light" className="text-dark">{e.empStatus}</Badge>
                                        : <span className="text-muted">—</span>}
                                </td>
                                <td>
                                    {e.roleId == null
                                        ? <Badge color="warning">Unassigned</Badge>
                                        : <Badge color="info">{e.roleName ?? `Role ${e.roleId}`}</Badge>}
                                </td>
                                <td>
                                    {e.epoLimit == null
                                        ? <Badge color="warning">Unassigned</Badge>
                                        : <span className="fw-semibold">${Number(e.epoLimit).toLocaleString()}</span>}
                                </td>
                                <td>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="100"
                                        bsSize="sm"
                                        value={inputValue}
                                        placeholder="(blank = clear)"
                                        invalid={pending !== undefined && !pendingValid}
                                        onChange={(ev) =>
                                            setPendingLimit((p) => ({
                                                ...p,
                                                [e.empId]: ev.target.value
                                            }))
                                        }
                                        disabled={savingId === e.empId}
                                    />
                                </td>
                                <td className="text-end">
                                    <Button
                                        color={dirty ? 'primary' : 'light'}
                                        size="sm"
                                        disabled={!dirty || savingId === e.empId}
                                        onClick={() => handleSave(e)}
                                    >
                                        {savingId === e.empId ? (
                                            <>
                                                <Spinner size="sm" /> Saving…
                                            </>
                                        ) : (
                                            <>
                                                <i className="ri-save-line align-bottom me-1"></i>
                                                Save
                                            </>
                                        )}
                                    </Button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

    // ─────────────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────────────
    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-money-dollar-circle-line"
                    title="Employee EPO Limit Assignment"
                    subtitle="Assign per-user EPO approval limits"
                    tone="violet"
                    breadcrumb={[
                        { label: "IT Admin", to: "/admin" },
                        { label: "Employee EPO Limit Assignment" },
                    ]}
                />

                {/* Configurable USA President/CEO approval threshold */}
                <Row className="mb-3">
                    <Col xs={12}>
                        <Card>
                            <CardBody>
                                <div className="d-flex align-items-center mb-2">
                                    <i className="ri-flag-2-line align-middle me-2 text-primary" style={{ fontSize: 20 }} />
                                    <h5 className="mb-0">USA President / CEO Approval Threshold</h5>
                                </div>
                                <p className="text-muted mb-3">
                                    EPOs in the USA whose total exceeds this amount also require President and CEO
                                    approval. EPOs at or below it follow the supervisor chain only. (India is exempt.)
                                </p>
                                {thresholdError ? (
                                    <div className="text-danger mb-2">{thresholdError}</div>
                                ) : null}
                                <Row className="g-2 align-items-center">
                                    <Col xs="auto">
                                        <div className="input-group" style={{ maxWidth: 260 }}>
                                            <span className="input-group-text">$</span>
                                            <Input
                                                type="number"
                                                min="0"
                                                step="100"
                                                value={threshold}
                                                placeholder={thresholdLoading ? 'Loading…' : 'e.g. 5000'}
                                                disabled={thresholdLoading || thresholdSaving}
                                                onChange={(ev) => setThreshold(ev.target.value)}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs="auto">
                                        <Button
                                            color="primary"
                                            disabled={thresholdLoading || thresholdSaving}
                                            onClick={handleSaveThreshold}
                                        >
                                            {thresholdSaving ? (<><Spinner size="sm" /> Saving…</>) : (<><i className="ri-save-line align-bottom me-1"></i> Save threshold</>)}
                                        </Button>
                                    </Col>
                                    {thresholdOriginal != null ? (
                                        <Col xs="auto">
                                            <span className="text-muted">Current: <span className="fw-semibold">${Number(thresholdOriginal).toLocaleString()}</span></span>
                                        </Col>
                                    ) : null}
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Configurable mileage reimbursement rate (Batch F) */}
                <Row className="mb-3">
                    <Col xs={12}>
                        <Card>
                            <CardBody>
                                <div className="d-flex align-items-center mb-2">
                                    <i className="ri-roadster-line align-middle me-2 text-primary" style={{ fontSize: 20 }} />
                                    <h5 className="mb-0">Mileage Reimbursement Rate</h5>
                                </div>
                                <p className="text-muted mb-3">
                                    The per-mile rate used to compute the mileage allowance on every EPO. Changing it
                                    here updates the rate everyone sees on the Create EPO form and the Review window.
                                </p>
                                {mileageError ? (
                                    <div className="text-danger mb-2">{mileageError}</div>
                                ) : null}
                                <Row className="g-2 align-items-center">
                                    <Col xs="auto">
                                        <div className="input-group" style={{ maxWidth: 220 }}>
                                            <span className="input-group-text">$</span>
                                            <Input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={mileageRate}
                                                placeholder={mileageLoading ? 'Loading…' : 'e.g. 0.70'}
                                                disabled={mileageLoading || mileageSaving || !isItAdmin}
                                                onChange={(ev) => setMileageRate(ev.target.value)}
                                            />
                                            <span className="input-group-text">/mile</span>
                                        </div>
                                    </Col>
                                    <Col xs="auto">
                                        <Button
                                            color="primary"
                                            disabled={mileageLoading || mileageSaving || !isItAdmin}
                                            onClick={handleSaveMileageRate}
                                        >
                                            {mileageSaving ? (<><Spinner size="sm" /> Saving…</>) : (<><i className="ri-save-line align-bottom me-1"></i> Save rate</>)}
                                        </Button>
                                    </Col>
                                    {mileageRateOriginal != null ? (
                                        <Col xs="auto">
                                            <span className="text-muted">Current: <span className="fw-semibold">${Number(mileageRateOriginal).toFixed(2)}/mile</span></span>
                                        </Col>
                                    ) : null}
                                    {!isItAdmin ? (
                                        <Col xs="auto"><span className="text-muted small">(View only — IT Admin can change)</span></Col>
                                    ) : null}
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Row>
                    <Col xs={12}>
                        <DataTableShell
                            title="Assign EPO limits to employees"
                            subtitle="Only the EPO limit is changed. No other employee fields are modified. Blank clears it; clearing falls back to the legacy formula."
                            rowCount={filtered.length}
                            countLabel="employees"
                            searchPlaceholder="Search EmpID, name, company, role…"
                            onRefresh={loadAll}
                            refreshing={loading}
                            loading={loading && employees.length === 0}
                            error={loadError}
                            empty={!loading && !loadError && filtered.length === 0}
                            emptyIcon="ri-inbox-line"
                            emptyTitle={search ? 'No matches' : 'No employees found'}
                            emptyMessage={
                                search
                                    ? 'No employees match your search. Try a different EmpID, name, company, or role.'
                                    : 'There are no employees to display.'
                            }
                            skeletonColumns={9}
                        >
                            {bulkToolbar}
                            {tableBody}
                        </DataTableShell>
                    </Col>
                </Row>
            </Container>
            <ToastContainer closeButton={false} limit={3} />
        </div>
    );
};

export default EmployeeEpoLimitAssignment;
